import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import MainView from "./MainView";
import LoadingSkeleton from "@/features/compare-players/components/organisms/LoadingSkeleton";
import { getPlayerCompositeMetricsArray } from "../../utils/getPlayerCompositeMetricsArray";

import { AnalyticsTracker } from "@/features/analytics";
import { useCurrentUser } from "@/features/auth";
import {
  PlayerBioData,
  PlayerDetailListPlayersByTeamIdsAndPositionIdsQuery,
  useCompareGetComparisonPlayerQuery,
  useRosterManagementListMyOrganizationsPlayersQuery,
  useWatchlistListWatchlistPlayersQuery,
  WatchlistPlayerFragment,
} from "@/generated/graphql";
import { PlayerCardData } from "@/types/playerCardTypes";
import useMobileScreen from "@/hooks/useMobileScreen";
import { usePlayerDetailQuery } from "@/hooks/usePlayerDetailQuery";
import useSetPageTitle from "@/hooks/useSetPageTitle";
import { useToastNotification } from "@/hooks/useToastNotification";
import { useTeamSelection } from "@/providers/TeamSelectionProvider";
import { useTheme } from "@/providers/ThemeProvider";
import { ICompositeMetric } from "@/stores/compositeMetric";
import { formatError } from "@/utils/formatError";
import { getRosterValues, labelToMetricId } from "@/utils/playerDetailCenter";

/**
 * Empty roster-comparison array used before any metric has been picked.
 * @remarks
 * Module-level purely so the "nothing selected yet" branch keeps one constant
 * identity instead of handing the modal a fresh array on every render. Treat it
 * as read-only — every downstream consumer of `rosterValues` is non-mutating.
 */
const NO_ROSTER_VALUES: number[] = [];

/**
 * The metric tile the user tapped, and which comparison column it belongs to.
 * @remarks
 * The column matters because the two grids describe different players: the
 * roster peers a metric is compared against are the ones sharing THAT player's
 * positions, and the peer list must exclude THAT player's own id.
 */
interface SelectedMetric {
  /** The composite metric's label, e.g. "Player Style Fit". */
  label: string;
  /** Which grid the tap came from. */
  column: "current" | "comparison";
}

/**
 * Orchestrates side-by-side player comparison with composite metric analysis.
 * Fetches player data, roster context, and watchlist players to enable metric-by-metric evaluation.
 * @module features/compare-players/screens/Main
 */
const MainContainer = () => {
  const router = useRouter();
  const currentUser = useCurrentUser();
  const toast = useToastNotification();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { playerId, watchlistId, watchlistName } = useLocalSearchParams<{
    playerId: string;
    watchlistId: string;
    watchlistName: string;
  }>();

  // `playerId` is momentarily undefined/"" while the route mounts and tears
  // down despite its `string` type, so the query must stay skipped until it
  // resolves — otherwise the poll fires with `id: ""`, the server rejects it,
  // and `onError` below pops a toast + navigates back on every tick (SE-5638).
  // Trimmed to match the hook backstop's definition of empty: a URL like
  // `/players/%20/compare` yields `id: " "` — truthy, but just as invalid.
  const { data, loading } = usePlayerDetailQuery({
    variables: { id: playerId ?? "" },
    skip: !playerId?.trim(),
    onError: error => {
      const e = formatError(error);
      toast.show({ action: "danger", description: e.message });
      router.back();
    },
  });
  const positionIds = useMemo(
    () =>
      data?.getPlayer?.positions?.map(p => p?.id).filter(p => !!p) as string[],
    [data?.getPlayer?.positions]
  );
  const { isLoaded: isTeamSelectionLoaded } = useTeamSelection();
  const {
    data: myRoster,
    fetchMore: myRosterFetchMore,
    loading: myRosterLoading,
  } = useRosterManagementListMyOrganizationsPlayersQuery({
    variables: { positionIds },
    notifyOnNetworkStatusChange: true,
    skip: !positionIds?.length || !isTeamSelectionLoaded,
  });
  const {
    data: watchlist,
    fetchMore: watchlistFetchMore,
    loading: watchlistLoading,
  } = useWatchlistListWatchlistPlayersQuery({
    variables: { watchlistId },
    notifyOnNetworkStatusChange: true,
    skip: !watchlistId,
  });
  const listMyRosterPlayers = useMemo(
    () =>
      myRoster?.listMyOrganizationsPlayers?.edges
        .map(edge => edge.node)
        .filter(item => item.id !== playerId) ?? [],
    [myRoster?.listMyOrganizationsPlayers?.edges, playerId]
  );

  const listWatchlistPlayers = useMemo(
    () =>
      watchlistId
        ? (watchlist?.listWatchlistPlayers?.edges
            .map(edge => edge.node)
            .filter(item => item.id !== playerId) ?? [])
        : undefined,
    [playerId, watchlist?.listWatchlistPlayers?.edges, watchlistId]
  );

  const handleLoadMore = useCallback(
    (type: string) => {
      if (type === "myroster") {
        const endCursor = myRoster?.listMyOrganizationsPlayers?.pageInfo
          .hasNextPage
          ? myRoster?.listMyOrganizationsPlayers?.pageInfo.endCursor
          : null;
        if (endCursor && !myRosterLoading) {
          myRosterFetchMore({ variables: { after: endCursor } });
        }
      } else {
        const endCursor = watchlist?.listWatchlistPlayers?.pageInfo.hasNextPage
          ? watchlist?.listWatchlistPlayers?.pageInfo.endCursor
          : null;
        if (endCursor && !watchlistLoading) {
          watchlistFetchMore({ variables: { after: endCursor } });
        }
      }
    },
    [
      myRoster?.listMyOrganizationsPlayers?.pageInfo.endCursor,
      myRoster?.listMyOrganizationsPlayers?.pageInfo.hasNextPage,
      myRosterFetchMore,
      myRosterLoading,
      watchlist?.listWatchlistPlayers?.pageInfo.endCursor,
      watchlist?.listWatchlistPlayers?.pageInfo.hasNextPage,
      watchlistFetchMore,
      watchlistLoading,
    ]
  );

  // The dropdown selection only carries the lightweight roster/watchlist card
  // node (PlayerCardData). We keep that node for the label/positions, and
  // re-fetch the player's FULL detail by id on select (SE-5377) so the charts +
  // composite-metrics column still get the season arrays + heavy bioData the
  // roster list no longer over-fetches.
  const [selectedComparisonNode, setSelectedComparisonNode] =
    useState<PlayerCardData | null>(null);
  const selectedComparisonId = selectedComparisonNode?.id;

  const { data: selectedComparisonData, loading: selectedComparisonLoading } =
    useCompareGetComparisonPlayerQuery({
      variables: { id: selectedComparisonId ?? "" },
      skip: !selectedComparisonId,
      fetchPolicy: "cache-first",
      // Surface refetch failures: without this, an error leaves the selection
      // label present while the charts/metrics stay silently empty.
      onError: error => {
        const e = formatError(error);
        toast.show({ action: "danger", description: e.message });
      },
    });

  // Full-detail object the charts read. Falls back to the lightweight node while
  // the full-detail query is in flight so the label/positions render immediately.
  const selectedComparisonPlayer: WatchlistPlayerFragment | null =
    selectedComparisonData?.getPlayer ?? null;

  const [explanationModalOpen, setExplanationModalOpen] = useState(false);
  /*
   * SE-7481 (sibling of SE-7447): hold only the user's SELECTION — which
   * metric was tapped, and in which of the two columns — and derive everything
   * downstream of a query result from live data.
   *
   * The previous shape called `getRosterValues` inside each metric-click
   * handler and stored the result in `useState`. `getRosterValues` returns `[]`
   * while `useRosterManagementListMyOrganizationsPlayersQuery` is still in
   * flight, and the metric grid is tappable during that window: the only render
   * gate on this screen is the player-detail query, so a cold load reaches a
   * clickable grid with the roster query unresolved. A modal opened then kept
   * an empty roster array for its whole lifetime — the peers landed in the
   * Apollo cache moments later and the comparison DotPlot never appeared. Only
   * closing and reopening took a fresh snapshot.
   *
   * Deriving instead means the loading -> resolved transition of the very query
   * this container already subscribes to re-renders the modal with real values,
   * and SE-7394's `rosterDataState` machine — which promotes `ready` over
   * `loading` — turns the spinner into the chart with no further change.
   *
   * `selectedMetricValue` stays in state on purpose: it is the tapped tile's
   * own `formattedValue`, which is user intent rather than derived query data.
   */
  const [selectedMetric, setSelectedMetric] = useState<SelectedMetric | null>(
    null
  );
  const [selectedMetricValue, setSelectedMetricValue] = useState<
    number | string | null
  >(null);

  const currentPlayerCompositeMetricsArray = useMemo(
    () =>
      getPlayerCompositeMetricsArray(
        data?.getPlayer?.bioData as PlayerBioData,
        currentUser?.organization?.currencyCode,
        false,
        isDark,
        true
      )
        .filter(metrics => metrics?.[0]?.label !== "Reports") // Don't show filter report in compare screen
        .reduce(
          (acc: ICompositeMetric[][], cur: ICompositeMetric[], _, arr) => {
            if (cur?.[0]?.label === "Passing") {
              const isExistPassingMetric = acc.find(
                it => it?.[0]?.label === "Passing"
              );

              if (!isExistPassingMetric) {
                const validPassingMetric = arr.find(
                  it =>
                    it?.[0]?.label === "Passing" &&
                    it?.[0]?.formattedValue !== "N/A"
                );
                if (validPassingMetric) {
                  return [...acc, validPassingMetric];
                } else {
                  return [...acc, cur];
                }
              } else {
                return acc;
              }
            }
            return [...acc, cur];
          },
          []
        ),
    [currentUser?.organization?.currencyCode, data?.getPlayer, isDark]
  );

  const selectedPlayerCompositeMetricsArray = useMemo(
    () =>
      getPlayerCompositeMetricsArray(
        selectedComparisonPlayer?.bioData as PlayerBioData,
        currentUser?.organization?.currencyCode,
        false,
        isDark,
        true
      ).filter(metrics => metrics?.[0]?.label !== "Reports"), // Don't show filter report in compare screen
    [currentUser?.organization?.currencyCode, selectedComparisonPlayer, isDark]
  );

  const { currentPlayerMetrics, selectedPlayerMetrics } = useMemo(() => {
    if (
      selectedComparisonPlayer &&
      currentPlayerCompositeMetricsArray &&
      currentPlayerCompositeMetricsArray.length > 0 &&
      selectedPlayerCompositeMetricsArray &&
      selectedPlayerCompositeMetricsArray.length > 0
    ) {
      const matchedArray = Array.from(
        { length: currentPlayerCompositeMetricsArray.length || 0 },
        (_, index) => {
          const currentPlayerMetric =
            currentPlayerCompositeMetricsArray?.[index];

          const selectedMetrics =
            selectedPlayerCompositeMetricsArray?.find(
              item =>
                item?.[0]?.label === currentPlayerMetric?.[0]?.label &&
                item?.[0]?.formattedValue !== "N/A"
            ) ||
            selectedPlayerCompositeMetricsArray?.find(
              it => it?.[0]?.label === currentPlayerMetric?.[0]?.label
            );

          if (
            currentPlayerMetric &&
            selectedMetrics &&
            (currentPlayerMetric?.[0]?.formattedValue !== "N/A" ||
              selectedMetrics?.[0]?.formattedValue !== "N/A")
          ) {
            return [currentPlayerMetric, selectedMetrics];
          }
          return [];
        }
      ).filter(item => item.length > 0);

      return {
        currentPlayerMetrics:
          matchedArray.length > 0 ? matchedArray?.map(([a]) => a) : [],
        selectedPlayerMetrics:
          matchedArray.length > 0 ? matchedArray.map(([_, b]) => b) : [],
      };
    }
    return {
      currentPlayerMetrics: currentPlayerCompositeMetricsArray.filter(
        item => item[0].formattedValue !== "N/A"
      ),
      selectedPlayerMetrics: [],
    };
  }, [
    selectedComparisonPlayer,
    currentPlayerCompositeMetricsArray,
    selectedPlayerCompositeMetricsArray,
  ]);

  const handleSelectedComparison = useCallback((player: PlayerCardData) => {
    // Store only the lightweight card node; the on-select full-detail query
    // (keyed on its id) hydrates the season arrays + heavy bioData the charts read.
    setSelectedComparisonNode(player);
  }, []);

  // Identity/positions source for handlers + the label. Prefer the resolved
  // full-detail object; fall back to the lightweight node while it loads so the
  // modal/label work the instant a player is picked.
  const selectedComparisonIdentity =
    selectedComparisonPlayer ?? selectedComparisonNode;

  const handleCurrentPlayerMetricClick = useCallback(
    (compositeMetric: ICompositeMetric) => {
      setSelectedMetric({ label: compositeMetric.label, column: "current" });
      setSelectedMetricValue(compositeMetric.formattedValue);
      setExplanationModalOpen(true);
    },
    []
  );

  const handleSelectedPlayerMetricClick = useCallback(
    (compositeMetric: ICompositeMetric) => {
      setSelectedMetric({ label: compositeMetric.label, column: "comparison" });
      setSelectedMetricValue(compositeMetric.formattedValue);
      setExplanationModalOpen(true);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setExplanationModalOpen(false);
    setSelectedMetric(null);
    setSelectedMetricValue(null);
  }, []);

  const selectedComparisonPlayerLabel = useMemo(
    () =>
      selectedComparisonIdentity ? selectedComparisonIdentity.name : undefined,
    [selectedComparisonIdentity]
  );

  // Create mock listPlayerSameTeamAndPosition data for modal
  const mockListPlayerSameTeamAndPosition = useMemo(
    () =>
      ({
        listPlayersByTeamIdsAndPositionIds: {
          edges:
            listMyRosterPlayers?.map(player => ({
              node: {
                id: player.id,
                bioData: player.bioData,
              },
            })) ?? [],
        },
      }) as PlayerDetailListPlayersByTeamIdsAndPositionIdsQuery,
    [listMyRosterPlayers]
  );

  /*
   * SE-7481: the subject of the open modal — whose positions decide how a
   * label maps to a metric id, and whose id is excluded from the peer set.
   * Both grids feed the SAME modal, so the tapped column picks the subject.
   */
  const selectedMetricSubject = useMemo(() => {
    if (!selectedMetric) return null;

    return selectedMetric.column === "current"
      ? {
          id: data?.getPlayer?.id,
          positions:
            data?.getPlayer?.positions?.map(p => ({ name: p?.name })) ?? null,
        }
      : {
          id: selectedComparisonIdentity?.id,
          positions:
            selectedComparisonIdentity?.positions?.map(p => ({
              name: p?.name ?? null,
            })) ?? null,
        };
  }, [
    selectedMetric,
    data?.getPlayer?.id,
    data?.getPlayer?.positions,
    selectedComparisonIdentity?.id,
    selectedComparisonIdentity?.positions,
  ]);

  const selectedMetricId = useMemo(
    () =>
      selectedMetric
        ? labelToMetricId(
            selectedMetric.label,
            selectedMetricSubject?.positions ?? null
          )
        : null,
    [selectedMetric, selectedMetricSubject?.positions]
  );

  const selectedRosterValues = useMemo(
    () =>
      selectedMetric
        ? getRosterValues(
            selectedMetric.label,
            mockListPlayerSameTeamAndPosition,
            selectedMetricSubject?.positions ?? null,
            selectedMetricSubject?.id
          )
        : NO_ROSTER_VALUES,
    [
      selectedMetric,
      mockListPlayerSameTeamAndPosition,
      selectedMetricSubject?.positions,
      selectedMetricSubject?.id,
    ]
  );

  /*
   * SE-7481: the modal never received this flag, so SE-7394's chart-region
   * machine saw `undefined` and resolved to `empty` — a confident "No roster
   * comparison data available" over data that was still on the wire. The
   * spinner branch was unreachable on this screen.
   *
   * It is not simply `myRosterLoading`: the roster query is SKIPPED until the
   * team selection loads, and a skipped query reports `loading: false`. Peers
   * that are expected but not yet requested are still "fetching" as far as the
   * reader is concerned.
   */
  const isFetchingRosterComparison =
    myRosterLoading || (!isTeamSelectionLoaded && !!positionIds?.length);

  const isSmall = useMobileScreen();

  useSetPageTitle("Compare Players");

  // SE-4010: feature-scoped analytics — carries the subject player_id the
  // generic route-level screen_view cannot express. Once per subject player.
  useEffect(() => {
    if (!playerId) return;
    void AnalyticsTracker.player.logPlayerComparisonViewed({
      player_id: playerId,
    });
  }, [playerId]);

  // Show full-screen skeleton on initial load before any content
  if (loading && !data?.getPlayer) {
    return <LoadingSkeleton isSmall={isSmall} />;
  }

  return (
    <MainView
      player={data?.getPlayer}
      loading={loading}
      isSmall={isSmall}
      watchlistName={watchlistName}
      onLoadMore={handleLoadMore}
      myRosterLoading={myRosterLoading}
      watchlistLoading={watchlistLoading}
      myRoster={listMyRosterPlayers}
      playerSelect={selectedComparisonPlayerLabel}
      watchList={listWatchlistPlayers}
      selectedComparisonPlayer={selectedComparisonPlayer}
      selectedComparisonLoading={selectedComparisonLoading}
      currentPlayerCompositeMetricsArray={currentPlayerMetrics}
      selectedPlayerCompositeMetricsArray={selectedPlayerMetrics}
      onSelectComparisonPlayer={handleSelectedComparison}
      explanationModalOpen={explanationModalOpen}
      selectedMetricId={selectedMetricId}
      selectedMetricValue={selectedMetricValue}
      selectedRosterValues={selectedRosterValues}
      listPlayerSameTeamAndPosition={mockListPlayerSameTeamAndPosition}
      isFetchingListPlayerSameTeamAndPosition={isFetchingRosterComparison}
      onCurrentPlayerMetricClick={handleCurrentPlayerMetricClick}
      onSelectedPlayerMetricClick={handleSelectedPlayerMetricClick}
      onCloseModal={handleCloseModal}
    />
  );
};

export default MainContainer;
